import { useNavigate, useParams } from 'react-router-dom';
import { Suspense, useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import {
    ActuatorAccessoriesPageData,
    ActuatorAccessoriesPageVariables,
    getActuatorAccessoriesPageConfig,
} from 'src/actuators/accessories/ActuatorAccessoriesPageMapping.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { skipToken } from '@apollo/client';
import { composeFilter } from 'src/utils/composeFilter.ts';
import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { Query } from 'src/modules/common/components/Query/Query.tsx';
import { ActuatorsCheckoutPage } from 'src/actuators/checkout/ActuatorsCheckoutPage.tsx';

export const PrefetchActuatorCheckout = () => {
    const {
        actuatorLabel,
    } = useParams();

    if (!actuatorLabel) {
        throw new Error('No Actuator selected');
    }

    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const pageConfig = useMemo(() => getActuatorAccessoriesPageConfig(userSettings.currentRegion), [userSettings]);

    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const navigate = useNavigate();

    const [
        searchParams,
        filter,
    ] = useFilterLogic([
        ...pageConfig.mainFilterConfig,
        ...pageConfig.additionalFilterConfig,
    ]);

    useCheckMainFilter({
        filter,
        searchParams,
        navigate,
        filterConfig: [],
    });

    const variables = useMemo((): { variables: ActuatorAccessoriesPageVariables } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }
        return {
            variables: {
                filter: composeFilter(
                    mandatoryFilterConfig.config,
                    mandatoryFilterConfig.values,
                    userSettings.currentLocale,
                ),
                label: actuatorLabel,
                locale: userSettings.currentLocale,
            },
        };
    }, [
        mandatoryFilterConfig,
        filter,
        actuatorLabel,
        userSettings,
    ]);

    return (
        <Suspense fallback={<SkeletonMainLayout breadcrumbs={false}/>}>
            <Query<ActuatorAccessoriesPageData, ActuatorAccessoriesPageVariables>
                query={pageConfig.query}
                variables={variables}
            >
                {({ data }) => {
                    if (data === undefined) {
                        return (<SkeletonMainLayout/>);
                    }
                    return (
                        <ActuatorsCheckoutPage
                            navigate={navigate}
                            filter={filter}
                            searchParams={searchParams}
                            data={data}
                        />
                    );
                }}
            </Query>
        </Suspense>
    );
};
